import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Edit2, Layers3, Plus, Tag, Trash2 } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Header } from "../components/layout/Header";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Textarea } from "../components/ui/textarea";
import { useAuth } from "../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
const MASTER_PRODUCTS_POLL_INTERVAL_MS = 8000;

const initialProductForm = {
  merchant_id: "",
  name: "",
  category: "",
  price: "",
  cost_price: "",
  image: "",
  description: "",
  stock: 0,
  sort_order: 0,
  is_active: true,
};

const initialCategoryForm = {
  name: "",
  sort_order: 0,
  is_active: true,
};

const formatRupiah = (amount) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(Number(amount || 0));

const parseCurrencyInput = (raw) => {
  const str = String(raw || "");
  const cleaned = str.replace(/\./g, "").replace(/,/g, ".");
  const digitsOnly = cleaned.replace(/[^0-9.]/g, "");
  const parts = digitsOnly.split(".");
  if (parts.length > 2) {
    return parts[0] + "." + parts.slice(1).join("");
  }
  return digitsOnly;
};

const modalFieldClass =
  "rounded-sm border-transparent bg-white/[0.04] text-white shadow-none ring-1 ring-white/[0.035] transition-colors focus-visible:ring-white/15";
const modalSelectClass =
  "h-10 w-full rounded-sm border-0 bg-white/[0.04] px-3 text-white outline-none ring-1 ring-white/[0.035] transition-colors focus:ring-white/15 [&_option]:bg-white [&_option]:text-black";
const modalDialogClass =
  "border-transparent bg-zinc-900 text-white shadow-2xl ring-1 ring-white/[0.04]";
const modalOutlineButtonClass =
  "flex-1 rounded-sm border-transparent bg-white/[0.03] text-white ring-1 ring-white/[0.04] hover:bg-white/[0.06]";

export default function MasterProducts() {
  const { user } = useAuth();
  const isAdmin = String(user?.role || "").trim().toLowerCase() === "admin";
  const currentMerchantId = String(user?.merchant_id || "").trim();
  const canRequestStock = isAdmin;
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [merchantFilter, setMerchantFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isStockModalOpen, setIsStockModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [productForm, setProductForm] = useState(initialProductForm);
  const [stockForm, setStockForm] = useState({ quantity: "", cost_amount: "", notes: "" });
  const [categoryForm, setCategoryForm] = useState(initialCategoryForm);
  const [selectedImageFile, setSelectedImageFile] = useState(null);

  const categoryMap = useMemo(
    () =>
      categories.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [categories]
  );

  const merchantMap = useMemo(
    () =>
      merchants.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
      }, {}),
    [merchants]
  );

  const activeCategories = useMemo(
    () => categories.filter((item) => item.is_active !== false),
    [categories]
  );

  const sortedProducts = useMemo(
    () =>
      [...products].filter((product) => String(product.sale_type || "OWN").toUpperCase() !== "CONSIGNMENT").sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [products]
  );

  const visibleProducts = useMemo(
    () =>
      sortedProducts.filter((product) => {
        if (!isAdmin) return String(product.merchant_id || "").trim() === currentMerchantId;
        if (!merchantFilter || merchantFilter === "all") return true;
        return String(product.merchant_id || "") === merchantFilter;
      }),
    [currentMerchantId, isAdmin, merchantFilter, sortedProducts]
  );

  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || "").localeCompare(String(b.name || ""));
      }),
    [categories]
  );

  const fetchAllData = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const [productsRes, categoriesRes, merchantsRes] = await Promise.all([
        axios.get(`${API}/products`, { withCredentials: true }),
        axios.get(`${API}/product-categories`, { withCredentials: true }),
        isAdmin
          ? axios.get(`${API}/merchants`, { withCredentials: true })
          : Promise.resolve({ data: [] }),
      ]);
      setProducts(Array.isArray(productsRes.data) ? productsRes.data : []);
      setCategories(Array.isArray(categoriesRes.data) ? categoriesRes.data : []);
      const merchantList = isAdmin && Array.isArray(merchantsRes.data) ? merchantsRes.data : [];
      setMerchants(merchantList);
      if (!isAdmin) {
        if (merchantFilter !== "all") setMerchantFilter("all");
        return;
      }
      if (merchantFilter !== "all" && !merchantList.some((item) => item.id === merchantFilter)) {
        setMerchantFilter("all");
      }
    } catch (error) {
      console.error("Failed to fetch master product data:", error);
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [isAdmin, merchantFilter]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchAllData(false);
    }, MASTER_PRODUCTS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [fetchAllData]);

  const resolveImageUrl = (image) => {
    if (!image) return "";
    if (String(image).startsWith("http://") || String(image).startsWith("https://")) return image;
    return `${BACKEND_URL}${image}`;
  };

  const openCreateProduct = () => {
    if (!isAdmin) return;
    setEditingProduct(null);
    setSelectedImageFile(null);
    setProductForm({
      ...initialProductForm,
      merchant_id: "",
    });
    setIsProductModalOpen(true);
  };

  const openEditProduct = (product) => {
    if (!isAdmin) return;
    setEditingProduct(product);
    setSelectedImageFile(null);
    const fallbackMerchantId = merchantFilter !== "all" ? merchantFilter : merchants[0]?.id || "";
    setProductForm({
      merchant_id: product.merchant_id || fallbackMerchantId,
      name: product.name || "",
      category: product.category || "",
      price: Number(product.price || 0),
      cost_price: Number(product.cost_price || 0),
      image: product.image || "",
      description: product.description || "",
      stock: Number(product.stock || 0),
      sort_order: Number(product.sort_order || 0),
      is_active: product.is_active !== false,
    });
    setIsProductModalOpen(true);
  };

  const openStockProduct = (product) => {
    if (!canRequestStock) return;
    setStockProduct(product);
    setStockForm({ quantity: "", cost_amount: "", notes: "" });
    setIsStockModalOpen(true);
  };

  const openCreateCategory = () => {
    if (!isAdmin) return;
    setEditingCategory(null);
    setCategoryForm(initialCategoryForm);
    setIsCategoryModalOpen(true);
  };

  const openEditCategory = (category) => {
    if (!isAdmin) return;
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || "",
      sort_order: Number(category.sort_order || 0),
      is_active: category.is_active !== false,
    });
    setIsCategoryModalOpen(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    if (!isAdmin) return;
    if (!window.confirm("Hapus kategori ini?")) return;
    try {
      await axios.delete(`${API}/product-categories/${categoryId}`, { withCredentials: true });
      fetchAllData(false);
    } catch (error) {
      console.error("Failed to delete category:", error);
      alert(error.response?.data?.detail || "Failed to delete category");
    }
  };

  const handleProductSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;

    if (!productForm.category) {
      alert("Pilih kategori produk terlebih dulu.");
      return;
    }
    if (!productForm.merchant_id) {
      alert("Pilih merchant produk terlebih dulu.");
      return;
    }

    let imagePath = productForm.image.trim();
    if (selectedImageFile) {
      const uploadData = new FormData();
      uploadData.append("file", selectedImageFile);
      const uploadRes = await axios.post(`${API}/uploads/product-image`, uploadData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      imagePath = uploadRes.data?.path || imagePath;
    }

    if (!imagePath) {
      alert("Gambar produk wajib diupload.");
      return;
    }

    const payload = {
      name: productForm.name.trim(),
      merchant_id: productForm.merchant_id,
      category: productForm.category,
      price: Number(productForm.price),
      cost_price: Number(productForm.cost_price || 0),
      image: imagePath,
      description: productForm.description.trim(),
      stock: editingProduct ? Number(editingProduct.stock || 0) : Number(productForm.stock),
      sort_order: Number(productForm.sort_order || 0),
      is_active: !!productForm.is_active,
    };

    if (editingProduct) {
      delete payload.stock;
    }

    try {
      let response;
      if (editingProduct) {
        response = await axios.put(`${API}/products/${editingProduct.id}`, payload, { withCredentials: true });
      } else {
        response = await axios.post(`${API}/products`, payload, { withCredentials: true });
      }
      setIsProductModalOpen(false);
      setEditingProduct(null);
      setSelectedImageFile(null);
      setProductForm(initialProductForm);
      fetchAllData(false);
      if (!editingProduct && response?.data?.status === "pending") {
        alert("Payment request tambah produk sudah dikirim ke admin. Produk akan muncul setelah admin approve.");
      }
    } catch (error) {
      console.error("Failed to save product:", error);
      alert(error.response?.data?.detail || "Failed to save product");
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    const payload = {
      name: categoryForm.name.trim(),
      sort_order: Number(categoryForm.sort_order || 0),
      is_active: !!categoryForm.is_active,
    };

    try {
      if (editingCategory) {
        await axios.put(`${API}/product-categories/${editingCategory.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/product-categories`, payload, { withCredentials: true });
      }
      setIsCategoryModalOpen(false);
      setEditingCategory(null);
      setCategoryForm(initialCategoryForm);
      fetchAllData(false);
    } catch (error) {
      console.error("Failed to save category:", error);
      alert(error.response?.data?.detail || "Failed to save category");
    }
  };

  const handleAddStockSubmit = async (e) => {
    e.preventDefault();
    if (!canRequestStock) return;
    if (!stockProduct?.id) return;
    const quantity = Number(stockForm.quantity || 0);
    if (!quantity || quantity <= 0) {
      alert("Jumlah stok masuk wajib lebih dari 0.");
      return;
    }
    if (!Number(stockForm.cost_amount || 0) || Number(stockForm.cost_amount || 0) <= 0) {
      alert("Modal stok masuk wajib lebih dari 0.");
      return;
    }

    try {
      const response = await axios.post(
        `${API}/products/${stockProduct.id}/add-stock`,
        {
          quantity,
          cost_amount: Number(stockForm.cost_amount || 0),
          notes: stockForm.notes.trim(),
        },
        { withCredentials: true }
      );
      if (response?.data?.status === "pending" || !isAdmin) {
        alert("Payment request stok produk sudah dikirim ke admin. Stok akan bertambah setelah admin approve.");
      } else {
        alert("Stok produk berhasil ditambahkan.");
      }
      setIsStockModalOpen(false);
      setStockProduct(null);
      setStockForm({ quantity: "", cost_amount: "", notes: "" });
      fetchAllData(false);
    } catch (error) {
      console.error("Failed to add product stock:", error);
      alert(error.response?.data?.detail || "Gagal menambah stok produk");
    }
  };

  return (
    <DashboardLayout>
      <Header title="Master Product" />
      <div className="p-8 space-y-8" data-testid="master-products-page">
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F3D77A]">
                <Layers3 className="h-3.5 w-3.5" />
                Master Product
              </div>
              <h2 className="text-xl font-semibold text-white">Kelola kategori dan produk toko</h2>
              <p className="mt-1 text-sm text-zinc-500">
                {isAdmin
                  ? "Produk sekarang dikelola langsung oleh admin dan tersusun berdasarkan kategori."
                  : "Kasir dapat melihat data produk sesuai merchant, tanpa akses tambah atau edit produk."}
              </p>
            </div>
            {isAdmin && (
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={openCreateCategory}
                className="border-white/10 text-white hover:bg-zinc-800 rounded-sm"
              >
                <Tag className="mr-2 h-4 w-4" strokeWidth={1.5} />
                Tambah Kategori
              </Button>
              <Button
                onClick={openCreateProduct}
                disabled={activeCategories.length === 0 || merchants.length === 0}
                className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm"
                    data-testid="add-master-product-btn"
              >
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} />
                Tambah Produk
              </Button>
            </div>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="rounded-sm border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Total Kategori</p>
              <p className="mt-3 font-semibold text-3xl text-white">{sortedCategories.length}</p>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Kategori Aktif</p>
              <p className="mt-3 font-semibold text-3xl text-emerald-400">{activeCategories.length}</p>
            </div>
            <div className="rounded-sm border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Total Produk</p>
              <p className="mt-3 font-semibold text-3xl text-[#D4AF37]">{visibleProducts.length}</p>
            </div>
          </div>
        </section>

        {isAdmin && (
        <section className="rounded-sm border border-white/10 bg-zinc-900/50 p-6">
          <div className="mb-5 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Kategori Produk</h3>
              <p className="mt-1 text-sm text-zinc-500">Kategori nonaktif tidak bisa dipilih saat membuat produk baru.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {sortedCategories.map((category, index) => (
              <div key={category.id || index} className="rounded-sm border border-white/10 bg-black/20 p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-base font-semibold text-white">{category.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.18em] text-zinc-500">{category.id}</p>
                  </div>
                  <Badge
                    variant="outline"
                    className={`rounded-sm text-xs ${
                      category.is_active !== false
                        ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                        : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
                    }`}
                  >
                    {category.is_active !== false ? "Active" : "Inactive"}
                  </Badge>
                </div>
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-sm text-zinc-400">Order: {Number(category.sort_order || 0)}</p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openEditCategory(category)}
                      className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-zinc-800 hover:text-white"
                    >
                      <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-red-500/10 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {!loading && sortedCategories.length === 0 && (
              <div className="rounded-sm border border-dashed border-white/10 bg-black/20 p-4 text-sm text-zinc-500">
                Belum ada kategori. Tambahkan kategori dulu sebelum membuat produk.
              </div>
            )}
          </div>
        </section>
        )}

        <section className="rounded-sm border border-white/10 bg-zinc-900/50 overflow-hidden">
          <div className="flex flex-col gap-4 px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Daftar Produk</h3>
              <p className="mt-1 text-sm text-zinc-500">Setiap produk terhubung ke kategori dan merchant.</p>
            </div>
            {isAdmin && (
            <div className="w-full max-w-xs space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Filter Merchant</Label>
              <select
                value={merchantFilter}
                onChange={(e) => setMerchantFilter(e.target.value)}
                className={modalSelectClass}
                disabled={merchants.length === 0}
              >
                <option value="all">{merchants.length === 0 ? "Belum ada merchant" : "Semua Merchant"}</option>
                {merchants.map((merchant) => (
                  <option key={merchant.id} value={merchant.id}>
                    {merchant.name}
                  </option>
                ))}
              </select>
            </div>
            )}
          </div>

          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Produk</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Merchant</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Kategori</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Harga</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Harga Modal</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Stok</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4">Status</TableHead>
                {canRequestStock && (
                  <TableHead className="text-xs font-bold uppercase tracking-[0.1em] text-zinc-500 py-4 text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={canRequestStock ? 8 : 7} className="py-8 text-center">
                    <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
                  </TableCell>
                </TableRow>
              ) : visibleProducts.length > 0 ? (
                visibleProducts.map((product, index) => (
                  <TableRow key={product.id || index} className="border-white/5 hover:bg-zinc-900/50 transition-colors">
                    <TableCell className="py-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={resolveImageUrl(product.image)}
                          alt={product.name}
                          className="h-12 w-12 rounded-sm border border-white/10 object-cover"
                        />
                        <div>
                          <p className="text-sm font-medium text-white">{product.name}</p>
                          <p className="text-xs text-zinc-500">Order: {Number(product.sort_order || 0)}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">
                      {product.merchant_name || merchantMap[product.merchant_id]?.name || "-"}
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">
                      {categoryMap[product.category]?.name || product.category}
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{formatRupiah(product.price)}</TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">
                      <p>{formatRupiah(product.capital_total ?? product.cost_price)}</p>
                      <p className="mt-0.5 text-xs text-zinc-500">Unit: {formatRupiah(product.cost_price)}</p>
                    </TableCell>
                    <TableCell className="py-4 text-sm text-zinc-300">{Number(product.stock || 0)}</TableCell>
                    <TableCell className="py-4">
                      <Badge
                        variant="outline"
                        className={`rounded-sm text-xs ${
                          product.is_active !== false
                            ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                            : "bg-zinc-500/10 text-zinc-400 border-zinc-500/30"
                        }`}
                      >
                        {product.is_active !== false ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    {canRequestStock && (
                    <TableCell className="py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => openEditProduct(product)}
                          title="Edit produk"
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-sky-500/10 hover:text-sky-300"
                          data-testid={`edit-product-${index}`}
                        >
                          <Edit2 className="h-4 w-4" strokeWidth={1.8} />
                        </button>
                        <button
                          type="button"
                          onClick={() => openStockProduct(product)}
                          title="Tambah stok"
                          className="rounded-sm p-2 text-zinc-400 transition-colors hover:bg-emerald-500/10 hover:text-emerald-300"
                          data-testid={`add-stock-product-${index}`}
                        >
                          <Plus className="h-4 w-4" strokeWidth={1.8} />
                        </button>
                      </div>
                    </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={canRequestStock ? 8 : 7} className="py-8 text-center text-sm text-zinc-500">
                    Belum ada produk.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </section>

        <Dialog
          open={isProductModalOpen}
          onOpenChange={(open) => {
            setIsProductModalOpen(open);
            if (!open) {
              setEditingProduct(null);
              setSelectedImageFile(null);
            }
          }}
        >
          <DialogContent className={`flex max-h-[92vh] max-w-3xl flex-col overflow-hidden p-0 ${modalDialogClass}`}>
            <DialogHeader className="shrink-0 border-b border-white/10 px-6 py-5">
              <DialogTitle className="text-lg font-semibold">
                {editingProduct ? "Edit Product" : "Tambah Product"}
              </DialogTitle>
              <p className="mt-1 text-sm text-zinc-500">
                {editingProduct
                  ? "Ubah informasi produk. Stok tetap dikelola melalui fitur tambah stok."
                  : "Produk baru akan langsung ditambahkan ke Master Product."}
              </p>
            </DialogHeader>
            <form onSubmit={handleProductSubmit} className="flex min-h-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">1. Informasi Produk</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Merchant</Label>
                  <select
                    value={productForm.merchant_id}
                    onChange={(e) => setProductForm({ ...productForm, merchant_id: e.target.value })}
                    className={modalSelectClass}
                    required
                    data-testid="product-merchant-select"
                  >
                    <option value="" disabled hidden>
                      Pilih merchant
                    </option>
                    {merchants.map((merchant) => (
                      <option key={merchant.id} value={merchant.id}>
                        {merchant.name}
                      </option>
                    ))}
                  </select>
                  {merchants.length === 0 ? (
                    <p className="text-xs text-amber-400">Belum ada merchant. Tambahkan merchant dulu.</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Produk</Label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                    className={modalFieldClass}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Kategori</Label>
                  <select
                    value={productForm.category}
                    onChange={(e) => setProductForm({ ...productForm, category: e.target.value })}
                    className={modalSelectClass}
                    data-testid="product-category-select"
                    required
                  >
                    <option value="" disabled hidden>
                      Pilih kategori
                    </option>
                    {activeCategories.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                    {editingProduct && !activeCategories.some((item) => item.id === editingProduct.category) && editingProduct.category ? (
                      <option value={editingProduct.category}>
                        {categoryMap[editingProduct.category]?.name || editingProduct.category}
                      </option>
                    ) : null}
                  </select>
                  {activeCategories.length === 0 ? (
                    <p className="text-xs text-amber-400">Belum ada kategori aktif. Tambahkan kategori dulu.</p>
                  ) : null}
                </div>
              </div>
              </section>

              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">2. Harga dan Stok Awal</p>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Harga (Rp)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={productForm.price}
                    onChange={(e) => setProductForm({ ...productForm, price: parseCurrencyInput(e.target.value) })}
                    className={modalFieldClass}
                    placeholder="Contoh: 469.999,20"
                    required
                    data-testid="product-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Harga Modal (Rp)</Label>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={productForm.cost_price}
                    onChange={(e) => setProductForm({ ...productForm, cost_price: parseCurrencyInput(e.target.value) })}
                    className={modalFieldClass}
                    placeholder="Contoh: 469.999,20"
                    required
                    data-testid="product-cost-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Stok</Label>
                  <Input
                    type="number"
                    min={0}
                    value={productForm.stock}
                    onChange={(e) => setProductForm({ ...productForm, stock: e.target.value })}
                    className={modalFieldClass}
                    disabled={!!editingProduct}
                    required
                    data-testid="product-stock-input"
                  />
                  {editingProduct ? (
                    <p className="text-xs text-zinc-500">
                      Stok tidak bisa diedit manual. Tambahkan stok melalui Payment Request.
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-500">
                      Stok awal akan langsung masuk ke Master Product.
                    </p>
                  )}
                </div>
              </div>
              </section>

              <section className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#D4AF37]">3. Detail Produk</p>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Upload Gambar</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setSelectedImageFile(e.target.files?.[0] || null)}
                  className={modalFieldClass}
                  data-testid="product-image-input"
                />
                <p className="text-xs text-zinc-500">
                  {selectedImageFile
                    ? `File dipilih: ${selectedImageFile.name}`
                    : editingProduct
                    ? "Kosongkan jika tidak ingin ganti gambar."
                    : "Pilih file gambar produk."}
                </p>
                {productForm.image && !selectedImageFile && (
                  <img
                    src={resolveImageUrl(productForm.image)}
                    alt="Preview"
                    className="h-24 w-24 rounded-sm object-cover ring-1 ring-white/[0.04]"
                  />
                )}
              </div>

              <div className="mt-4 space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Deskripsi</Label>
                <Textarea
                  rows={4}
                  value={productForm.description}
                  onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                  className={modalFieldClass}
                  required
                  data-testid="product-description-input"
                />
              </div>
              </section>

              <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                {editingProduct ? (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort Order</Label>
                  <Input
                    type="number"
                    value={productForm.sort_order}
                    onChange={(e) => setProductForm({ ...productForm, sort_order: e.target.value })}
                    className={modalFieldClass}
                    data-testid="product-sort-order-input"
                  />
                </div>
                ) : null}
                <div className={`flex items-center justify-between ${editingProduct ? "mt-4" : ""}`}>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Active</Label>
                  <Switch
                    checked={productForm.is_active}
                    onCheckedChange={(checked) => setProductForm({ ...productForm, is_active: checked })}
                    data-testid="product-active-switch"
                  />
                </div>
              </div>
              </div>

              <div className="flex shrink-0 gap-3 border-t border-white/10 bg-zinc-900 px-6 py-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsProductModalOpen(false)}
                  className={modalOutlineButtonClass}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={activeCategories.length === 0 || merchants.length === 0}
                  className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]"
                  data-testid="product-submit-btn"
                >
                  {editingProduct ? "Update Product" : "Tambah Product"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isStockModalOpen}
          onOpenChange={(open) => {
            setIsStockModalOpen(open);
            if (!open) {
              setStockProduct(null);
              setStockForm({ quantity: "", cost_amount: "", notes: "" });
            }
          }}
        >
          <DialogContent className={`max-w-lg ${modalDialogClass}`}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {isAdmin ? "Tambah Stok Produk" : "Payment Request Stok Produk"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddStockSubmit} className="mt-4 space-y-4">
              <div className="rounded-sm border border-white/10 bg-black/20 p-4">
                <p className="text-xs uppercase tracking-[0.2em] text-zinc-500">Produk</p>
                <p className="mt-2 text-lg font-semibold text-white">{stockProduct?.name || "-"}</p>
                <p className="mt-1 text-sm text-zinc-400">
                  Stok sekarang: <span className="font-semibold text-[#F3D77A]">{Number(stockProduct?.stock || 0)}</span>
                </p>
                <p className="mt-1 text-sm text-zinc-400">
                  Total modal sekarang:{' '}
                  <span className="font-semibold text-[#F3D77A]">
                    {formatRupiah(stockProduct?.capital_total ?? stockProduct?.cost_price)}
                  </span>
                </p>
                {Number(stockForm.quantity || 0) > 0 ? (
                  <p className="mt-1 text-sm text-emerald-300">
                    Setelah ditambah: {Number(stockProduct?.stock || 0) + Number(stockForm.quantity || 0)}
                  </p>
                ) : null}
                {Number(stockForm.cost_amount || 0) > 0 ? (
                  <p className="mt-1 text-sm text-emerald-300">
                    Total modal setelah ditambah:{' '}
                    {formatRupiah(
                      Number((stockProduct?.capital_total ?? stockProduct?.cost_price) || 0) +
                        Number(stockForm.cost_amount || 0)
                    )}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Jumlah Stok Masuk</Label>
                <Input
                  type="number"
                  min={1}
                  value={stockForm.quantity}
                  onChange={(e) => setStockForm({ ...stockForm, quantity: e.target.value })}
                  className={modalFieldClass}
                  placeholder="Contoh: 10"
                  required
                  data-testid="add-stock-quantity-input"
                />
                <p className="text-xs text-zinc-500">Jumlah ini akan ditambahkan ke stok lama, bukan mengganti stok lama.</p>
              </div>

              <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nominal Payment Request (Rp)</Label>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={stockForm.cost_amount}
                  onChange={(e) => setStockForm({ ...stockForm, cost_amount: parseCurrencyInput(e.target.value) })}
                  className={modalFieldClass}
                  placeholder="Contoh: 469.999,20"
                  required
                  data-testid="add-stock-cost-input"
                />
                <p className="text-xs text-zinc-500">
                  {isAdmin
                    ? "Nominal ini dipakai untuk menghitung total modal dan harga modal rata-rata produk."
                    : "Nominal ini akan dikirim sebagai Payment Request ke admin untuk approval stok."}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Catatan</Label>
                <Textarea
                  rows={3}
                  value={stockForm.notes}
                  onChange={(e) => setStockForm({ ...stockForm, notes: e.target.value })}
                  className={modalFieldClass}
                  placeholder="Opsional, contoh: restock dari supplier"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsStockModalOpen(false)}
                  className={modalOutlineButtonClass}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 rounded-sm bg-emerald-500 font-semibold text-black hover:bg-emerald-400"
                  data-testid="add-stock-submit-btn"
                >
                  {isAdmin ? "Tambah Stok" : "Kirim Payment Request"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Dialog
          open={isCategoryModalOpen}
          onOpenChange={(open) => {
            setIsCategoryModalOpen(open);
            if (!open) {
              setEditingCategory(null);
            }
          }}
        >
          <DialogContent className={`max-w-lg ${modalDialogClass}`}>
            <DialogHeader>
              <DialogTitle className="text-lg font-semibold">
                {editingCategory ? "Edit Kategori" : "Tambah Kategori"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCategorySubmit} className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Kategori</Label>
                <Input
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  className={modalFieldClass}
                  required
                />
                <p className="text-xs text-zinc-500">Slug kategori dibuat otomatis dari nama kategori.</p>
              </div>
              <div className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Sort Order</Label>
                  <Input
                    type="number"
                    value={categoryForm.sort_order}
                    onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: e.target.value })}
                    className={modalFieldClass}
                  />
                </div>
                <div className="flex items-center justify-between pt-5 md:justify-start md:gap-3">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Active</Label>
                  <Switch
                    checked={categoryForm.is_active}
                    onCheckedChange={(checked) => setCategoryForm({ ...categoryForm, is_active: checked })}
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCategoryModalOpen(false)}
                  className={modalOutlineButtonClass}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 rounded-sm bg-[#D4AF37] font-semibold text-black hover:bg-[#C59A27]">
                  {editingCategory ? "Update Kategori" : "Tambah Kategori"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
